import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import IterationFilter from '../filters/IterationFilter';
import TeamFilter from '../filters/TeamFilter';
import UserFilter from '../filters/UserFilter';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import { type RecordId } from 'javascripts/shared-types';
import moment from 'moment';
import Iteration from './Iteration';
import Project from './Project';
import User from './User';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksIterationBurndownAttributes } from './generated/BookmarksIterationBurndownAttributes';
import { type UnitType } from '../lib/Estimate';

interface Filters {
  iterations?: RecordId[];
  teams?: RecordId[];
  users?: RecordId[];
  estimation_type?: EstimateTypeFilterValue[];
}

interface Local {
  filterObjects: Record<string, ItemFilter<any>>;
  users: User[];
}

interface BookmarksIterationBurndown
  extends BookmarksIterationBurndownAttributes,
    HasBookmarkFilters<{
      iterations: IterationFilter;
      teams: TeamFilter;
      users: UserFilter;
      estimation_type: EstimateTypeFilter;
    }> {
  filters: Filters;
  local: Partial<Local>;
  availableIterations(): Promise<Iteration[]>;
  getIteration(params: {
    iterationId?: string;
  }): Promise<Iteration | undefined>;
}
@model
@HasBookmarkFilters
class BookmarksIterationBurndown extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksIterationBurndown' = 'BookmarksIterationBurndown';
  get typename(): 'BookmarksIterationBurndown' {
    return 'BookmarksIterationBurndown';
  }

  static internalTypename: 'Bookmarks::IterationBurndown' =
    'Bookmarks::IterationBurndown';
  get internalTypename(): 'Bookmarks::IterationBurndown' {
    return 'Bookmarks::IterationBurndown';
  }

  static bookmarkName = 'Bookmarks::IterationBurndown';

  static inputType = 'IterationBurndownAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  initialize() {
    this.local = { filterObjects: {} };
    this.filters = this.filters || {};
  }

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [iterations, teams, users] = await Promise.all([
      this.availableIterations(),
      this.selectedTeams(),
      this.availableUsers(),
    ]);

    let iteration: Iteration | undefined;
    if (iterations && this.filters.iterations) {
      const iterationId = this.filters.iterations[0];
      iteration = iterations.find(i => i.id === iterationId);
    }

    if (!iteration && iterations && iterations.length > 0) {
      iteration = iterations[0];
    }

    let defaultEstimationType: EstimateTypeFilterValue = 'count';

    if (iteration?.defaultEstimationType === 'original') {
      defaultEstimationType = 'originalEstimate';
    }

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems: this.filters.estimation_type || [defaultEstimationType],
      capacityUnits: iteration?.originalEstimate?.units,
      showRemainingEstimate: window.currentAccount?.teamworkOrDevelopAdvanced,
    });

    const iterationFilter = new IterationFilter(
      this.filters.iterations || [],
      (iterations ?? []).map(nameMap)
    );
    const userFilter = new UserFilter(
      this.filters.users || [],
      users.map(nameMap)
    );

    return {
      iterations: iterationFilter,
      teams: new TeamFilter(
        teams.map(([, id]) => id),
        teams
      ),
      users: userFilter,
      estimation_type: estimateTypeFilter,
    };
  }

  canFilter(scope: string) {
    return true;
  }

  async selectedTeams(): Promise<[string, RecordId][]> {
    if (!this.project?.isProductLine) {
      return [];
    }
    const childIds = this.project.children.map(c => c.id);
    const teamIds = this.filters.teams?.length ? this.filters.teams : childIds;

    if (teamIds.length > 0) {
      try {
        const teams = await Project.select(['id', 'name'])
          .where({ id: teamIds })
          .all();
        return teams.map(t => [t.name, t.id]);
      } catch (error) {
        console.error('selectedTeams - error fetching teams:', error);
        return [];
      }
    }
    return [];
  }

  /**
   * Load all iterations - custom implementation for team lines
   */
  async availableIterations(): Promise<Iteration[]> {
    // If this is a team line, fetch iterations from selected teams
    if (this.project?.isProductLine) {
      const teams = await this.selectedTeams();
      if (teams.length > 0) {
        const teamIds = teams.map(([, id]) => id);

        // Fetch iterations for each team separately and combine results
        const allIterations = await Promise.all(
          teamIds.map(async teamId => {
            return Iteration.select(
              'id',
              'name',
              'status',
              'duration',
              'startDate',
              'endDate',
              'createdAt'
            )
              .merge({ originalEstimate: ['units', 'value'] })
              .where({ projectId: teamId, status: [20, 30] })
              .order({ startDate: 'DESC' })
              .all();
          })
        );

        // Flatten the array of ModelCollections into a single array of iterations
        const iterations = allIterations.flatMap(collection =>
          Array.from(collection)
        );

        return iterations.filter(i =>
          moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
        );
      }
      return [];
    }

    // Single team mode - use standard implementation
    const collection = await Iteration.select(
      'id',
      'name',
      'status',
      'duration',
      'startDate',
      'endDate',
      'createdAt'
    )
      .merge({ originalEstimate: ['units', 'value'] })
      .where({ projectId: this.projectId, status: [20, 30] })
      .order({ startDate: 'DESC' })
      .all();

    const iterations = Array.from(collection);
    return iterations.filter(i =>
      moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
    );
  }

  /**
   * Get a specific iteration by ID
   */
  async getIteration({
    iterationId,
  }: {
    iterationId?: string;
  }): Promise<Iteration | undefined> {
    const iterations = await this.availableIterations();
    if (!iterationId && iterations.length > 0) {
      return iterations[0];
    }
    return iterations.find(i => i.id === iterationId);
  }

  /**
   * Load all the users available to this team
   */
  async availableUsers() {
    if (!this.local.users)
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId, includeAll: true })
        .per(100)
        .all();
    return this.local.users;
  }

  /**
   * Load the selected iteration using the available iterations
   */
  async iteration(): Promise<Iteration | undefined> {
    const iterationId = this.getFilter('iterations')?.single();
    let result = await this.getIteration({ iterationId });

    // If the saved iteration doesn't exist (e.g., from a different team), fall back to first available
    if (!result && iterationId) {
      result = await this.getIteration({ iterationId: undefined });
    }

    return result;
  }

  /**
   * Load the selected user
   */
  async user() {
    const userId = this.getFilter('users')?.single();
    if (!userId) return;

    const users = await this.availableUsers();
    return users.find(u => u.id === userId);
  }

  /**
   * Load the selected unit
   */
  async units(): Promise<UnitType> {
    const units = this.getFilter('estimation_type')?.single();

    if (units === 'count') {
      return 'COUNT';
    } else {
      const iteration = await this.iteration();
      return iteration?.originalEstimate?.units ?? 'POINTS';
    }
  }

  /**
   * Get open iterations from each selected team (for team line mode)
   */
  async openIterationsFromTeams(): Promise<Iteration[]> {
    if (!this.project?.isProductLine) {
      return [];
    }

    const teams = await this.selectedTeams();
    if (teams.length === 0) {
      return [];
    }

    const teamIds = teams.map(([, id]) => id);

    // Fetch the currently open iteration (status 20 = started) from each team
    const allIterations = await Promise.all(
      teamIds.map(async teamId => {
        return Iteration.select(
          'id',
          'name',
          'status',
          'duration',
          'startDate',
          'endDate',
          'createdAt'
        )
          .merge({ originalEstimate: ['units', 'value'] })
          .where({ projectId: teamId, status: 20 }) // Only started iterations
          .order({ startDate: 'DESC' })
          .all();
      })
    );

    // Flatten and get the first (most recent) open iteration from each team
    return allIterations
      .flatMap(collection => Array.from(collection))
      .filter(i =>
        moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
      );
  }

  /**
   * Fetch every single record event for the selected iteration
   *
   * @param filters Any filters to apply to the raw data fetch.
   */
  async recordEvents(filters: Record<string, any> = {}) {
    const iteration = await this.iteration();
    if (!iteration) {
      return undefined;
    }
    return iteration.eventsRaw(filters);
  }
}

export default BookmarksIterationBurndown;
